/**
 * **The prompt policy** — answers the `encounter_prompt`s a combatant (or its side's watchers/deciders) can receive mid-resolution: a Brace/interception
 * offer, an Overwatch move-checkpoint offer, a variable-damage-type choice, a System Trauma
 * "which of your equipment dies" pick, and a structure-loss interception offer. Deliberately a
 * **pure decision layer** ({@link decidePromptAnswer} + its per-class helpers) composed
 * with a **thin async driver** ({@link answerPrompt}) that rides `MatchHandle.submit()` — no new
 * seam method, exactly as `matchHandle.ts`'s header spells out ("answering a prompt is NOT a
 * separate primitive... re-submit the SAME intent with `choices[promptId]` merged in").
 *
 * **Independently useful even when humans drive turns**: nothing here is welded into
 * `autoplay.ts`'s loop. `autoplayActivation` takes this as a *pluggable* `promptPolicy` arg
 * (defaulting to {@link answerPrompt}) — a human-driven CLI/UI flow, or the direct-referee
 * `MatchHandle`, can call {@link answerPrompt} directly over any prompt it receives, autoplay
 * or not.
 *
 * **The five prompt classes, and why each decision composes an already-exported primitive
 * rather than re-deriving a rule:**
 *   - **Brace/interception offers** (`prompt.incomingDamage` set — Brace or an authored
 *     `on_damage_taken` reaction, `reactionRegistry.ts`): the engine has ALREADY rolled the
 *     triggering damage and computed both branches' final HP loss (`ifDeclined`/`ifReacted`) by
 *     the time the prompt reaches us — no EV toolkit call needed, no dice to model. Take the
 *     reaction iff it's strictly cheaper; decline a wash (preserves the reaction for a LATER
 *     opportunity this same round, e.g. a checkpoint offer, at zero cost since nothing was
 *     gained here).
 *   - **Move-checkpoint offers** (`isMoveCheckpointPromptId`, `checkpointRegistry.ts`) — TWO
 *     registered consumers, routed by `moveCheckpointConsumerId`, never by the shared
 *     `move_checkpoint:` shape alone:
 *       - **Overwatch** (`OVERWATCH_CHECKPOINT_ID`): "take iff EV-positive" over the watcher's
 *         live, non-Ordnance, non-Superheavy, in-**threat**-range weapons (`checkOverwatchRange` —
 *         the watcher's eligibility to fire at all, RAW p.105's Ordnance prohibition included;
 *         Superheavy fires only in a Barrage, Core p.70, which the referee's `checkSuperheavySkirmish`
 *         enforces on every `reaction: true` attack).
 *       - **The authored move-reaction offer** (`AUTHORED_MOVE_REACTION_ID`, e.g.
 *         `t_heavy_gunner:1`'s Covering Fire): "take iff EV-positive" over the SAME weapon
 *         filter, but gated by **normal weapon range** (`checkAttackRange`), not threat — a
 *         reaction attack is a real attack, not a threatened-square trigger, and RAW places no
 *         Threat-range constraint on it. Routing the authored offer through the Overwatch
 *         (threat) gate is a live footgun: a caster with a ranged weapon and a mover several
 *         spaces away then reads as able to strike only at threat-1 and always declines a shot
 *         it should take.
 *     Both share one core, {@link bestReactionAttack}, parameterized by which range gate applies
 *     — {@link evaluateAttackEv} argmaxes `expectedDamage` over the eligible weapons either
 *     way. **"Take" is not a single re-submit** —
 *     `checkpointRegistry.ts`'s own header doc names the "interleaving contract": the watcher's
 *     (or caster's) shot is a SIBLING resolution (its own `request_attack {reaction:true}`) that
 *     must resolve to completion BEFORE the parked move intent re-submits the recorded "take"
 *     answer. {@link answerPrompt} drives both submissions over the same `MatchHandle`.
 *     **An unrecognized checkpoint consumer id is `{ kind: "unhandled" }`, never a silent
 *     Overwatch default** — the explicit allow-list this module's own forward-safety doctrine
 *     (this header's closing paragraph) requires.
 *   - **Variable-damage-type choices** (`isVariableDamagePromptId`, `resolve.ts`): argmax
 *     {@link evaluateAttackEv}'s `expectedDamage` over the weapon's three possible resolved
 *     types (`VARIABLE_DAMAGE_TYPE_OPTIONS`) via `damageTypeOverride` — the toolkit already folds
 *     the target's own Resistance per type (`foldedResistance`), so a resistant target correctly
 *     steers the pick away from its resisted type.
 *   - **System Trauma picks** (`isSystemTraumaPromptId`, `tables.ts`): answer the engine's own
 *     deterministic pick — FIRST valid by index — by calling the engine's own
 *     `firstValidSystemTraumaOption` over `prompt.options`. `resolveStructureDamage`'s
 *     non-interactive leg (replay / CLI / the cascade callers that pass no `choices`) resolves
 *     through that SAME export, so the bot and the non-interactive engine make the same pick **by
 *     construction**, not by two implementations that happen to agree. There is no play-value read
 *     to make here yet: which of your own systems to lose is a judgement about the rest of the
 *     mission, and the EV toolkit models one exchange.
 *   - **Structure-loss interception offers** (`isStructureInterceptPromptId`,
 *     `structureIntercept.ts`): decline, with the engine's own `INTERCEPT_SAVE_OPTION` — the
 *     identical default `gatherStructureIntercept` takes on every non-interactive call. Unlike a
 *     Brace offer, the prompt carries **no `incomingDamage`** (the signal is raised before the
 *     Structure Damage table is rolled, so neither branch's cost is known yet), so there is
 *     nothing to compare and any "use it" policy would be invented rather than read. Whether a
 *     bot should ever burn a once-per-Full-Repair charge on a 1-in-6 is a real question and an
 *     open one; it is not answered here.
 *
 * **Forward-safety**: any prompt matching none of the five shapes is `{ kind: "unhandled" }` —
 * never guessed. That includes a System Trauma prompt with an empty option list and a structure
 * interception offer that does not carry `save`: an allow-list that halts on a shape it has not
 * been taught, never a nearest-neighbour default. `autoplay.ts` halts on that exactly like an
 * un-policied prompt always has.
 */
import {
  hasStatus,
  isCharacter,
  isItemDestroyed,
  sideOf,
  terrainVisibility,
  weaponInstanceKey,
  worldFor,
  superheavyBarsSkirmish,
  type Combatant,
  type DamageType,
  type EncounterState,
} from "@lancer-vtt/domain";
import {
  checkAttackRange,
  checkOverwatchRange,
  firstValidSystemTraumaOption,
  isMoveCheckpointPromptId,
  isStructureInterceptPromptId,
  isSystemTraumaPromptId,
  isVariableDamagePromptId,
  moveCheckpointConsumerId,
  moveCheckpointWatcherId,
  AUTHORED_MOVE_REACTION_ID,
  DECLINE_OPTION,
  INTERCEPT_SAVE_OPTION,
  MOVE_REACTION_DECLINE_OPTION,
  MOVE_REACTION_TAKE_OPTION,
  COVERING_FIRE_SPLIT_ID,
  OVERWATCH_CHECKPOINT_ID,
  OVERWATCH_DECLINE_OPTION,
  checkpointMoverId,
  OVERWATCH_TAKE_OPTION,
  VARIABLE_DAMAGE_TYPE_OPTIONS,
  SEED_OVERLAY,
  type BudgetVerdict,
  type LoadedOverlay,
} from "@lancer-vtt/rules";
import { CHOICE_CARRYING_INTENT_TYPES, mapForcedIntent, unwrapForced, type ChoiceCarryingIntent, type EncounterIntent, type PromptRequest, type RequestAttack } from "@lancer-vtt/shared";
import { evaluateAttackEv } from "./ev";
import type { MatchHandle, MatchObservation } from "./matchHandle";

/** The pure decision a Brace/interception offer resolves to — the option id to record. */
export function decideInterceptionOffer(prompt: PromptRequest): string {
  const dmg = prompt.incomingDamage;
  if (!dmg) return DECLINE_OPTION; // defensive — callers only reach this with incomingDamage set.
  if (dmg.ifReacted < dmg.ifDeclined) {
    // The accept option is "whichever option isn't decline" — robust across Brace's own
    // `BRACE_OPTION` ("brace") and an authored offer's `AUTHORED_ACCEPT_OPTION` ("use"), never
    // hardcoding one specific accept id.
    const accept = prompt.options.find((o) => o.id !== DECLINE_OPTION);
    if (accept) return accept.id;
  }
  return DECLINE_OPTION;
}

/** A range-eligibility gate a reaction-attack read filters candidate weapons through — {@link checkOverwatchRange} (threat range) for Overwatch, {@link checkAttackRange} (normal weapon reach) for an authored reaction offer. Both exports share this arg shape (mirroring their own `CheckOverwatchRangeArgs`/`CheckAttackRangeArgs`), which is exactly what lets {@link bestReactionAttack} take either interchangeably. */
type ReactionRangeCheck = (args: { state: EncounterState; attacker: string; target: string; weapon?: string }) => BudgetVerdict;

/**
 * The best live, non-Ordnance, non-Superheavy, `rangeCheck`-eligible weapon `watcherId` can fire at `moverId`
 * right now, by {@link evaluateAttackEv}'s `expectedDamage` — `undefined` when nothing usable is
 * in range (shouldn't happen given the offer was raised, but never assumed). A `variable`-damage-
 * type weapon is skipped (the EV toolkit needs a concrete type — a known gap, mirroring `ev.ts`'s
 * own documented scope). The shared core both move-checkpoint reads compose: Overwatch passes
 * {@link checkOverwatchRange} (threat), the authored move-reaction offer passes
 * {@link checkAttackRange} (normal reach); everything else (the weapon
 * filter, the EV argmax) is identical, since both actors' control axis is the same "fire my best
 * weapon at the mover if it helps me."
 */
function bestReactionAttack(args: {
  state: EncounterState;
  watcherId: string;
  moverId: string;
  overlay: LoadedOverlay;
  rangeCheck: ReactionRangeCheck;
}): { intent: RequestAttack; expectedDamage: number } | undefined {
  const { state, watcherId, moverId, overlay, rangeCheck } = args;
  const watcher = state.combatants[watcherId];
  const mover = state.combatants[moverId];
  if (!watcher || !mover) return undefined;
  const loadout = (watcher.base.weapons ?? []).filter(
    (w) => !isItemDestroyed(watcher.current.resources, weaponInstanceKey(w)) && !w.ordnance && !superheavyBarsSkirmish(w) && w.damage.type !== "variable",
  );
  let best: { ref: string; expectedDamage: number } | undefined;
  for (const w of loadout) {
    const ref = weaponInstanceKey(w);
    const verdict = rangeCheck({ state, attacker: watcherId, target: moverId, weapon: ref });
    if (!verdict.ok) continue;
    const ev = evaluateAttackEv({ state, attacker: watcher, target: mover, weapon: w, weaponRef: ref, overlay });
    if (!best || ev.expectedDamage > best.expectedDamage) best = { ref, expectedDamage: ev.expectedDamage };
  }
  if (!best) return undefined;
  const intent: RequestAttack = {
    type: "request_attack",
    attacker: watcherId,
    target: { kind: "unit", id: moverId },
    weapon: best.ref,
    accuracy: 0,
    reaction: true,
  };
  return { intent, expectedDamage: best.expectedDamage };
}

/** The pure decision a move-checkpoint offer resolves to — either a plain decline, or a "take" carrying the decider's own sibling `request_attack` intent to fire BEFORE the checkpoint answer re-submits (the interleaving contract, this file's header doc). Shared by both {@link decideOverwatchCheckpoint} and {@link decideMoveReactionOffer}. */
export type CheckpointDecision =
  | { kind: "decline" }
  | { kind: "take"; watcherAttack: RequestAttack; expectedDamage: number };

/** The Overwatch move-checkpoint read — take iff EV-positive over the watcher's live, non-Ordnance, non-Superheavy weapons in THREAT range ({@link checkOverwatchRange}), riding the shared {@link bestReactionAttack} core. */
export function decideOverwatchCheckpoint(args: {
  state: EncounterState;
  prompt: PromptRequest;
  overlay?: LoadedOverlay;
}): CheckpointDecision {
  const { state, prompt, overlay = SEED_OVERLAY } = args;
  const watcherId = moveCheckpointWatcherId(prompt.promptId);
  const moverId = checkpointMoverId(prompt.intent);
  if (!watcherId || !moverId) return { kind: "decline" };
  const best = bestReactionAttack({ state, watcherId, moverId, overlay, rangeCheck: checkOverwatchRange });
  if (!best || best.expectedDamage <= 0) return { kind: "decline" }; // "take iff EV-positive".
  return { kind: "take", watcherAttack: best.intent, expectedDamage: best.expectedDamage };
}

/** The authored move-reaction offer's read — take iff EV-positive over the caster's live, non-Ordnance, non-Superheavy weapons in NORMAL weapon range ({@link checkAttackRange}), mirroring {@link decideOverwatchCheckpoint}'s shape exactly except for that one gate. Content-agnostic by design: it does NOT replicate the unenforced "Heavy ranged weapon" constraint some future content pass might author. */
export function decideMoveReactionOffer(args: {
  state: EncounterState;
  prompt: PromptRequest;
  overlay?: LoadedOverlay;
}): CheckpointDecision {
  const { state, prompt, overlay = SEED_OVERLAY } = args;
  const casterId = moveCheckpointWatcherId(prompt.promptId);
  const moverId = checkpointMoverId(prompt.intent);
  if (!casterId || !moverId) return { kind: "decline" };
  const best = bestReactionAttack({ state, watcherId: casterId, moverId, overlay, rangeCheck: checkAttackRange });
  if (!best || best.expectedDamage <= 0) return { kind: "decline" }; // "take iff EV-positive".
  return { kind: "take", watcherAttack: best.intent, expectedDamage: best.expectedDamage };
}

/** The pure decision a variable-damage-type prompt resolves to — argmax {@link evaluateAttackEv}'s `expectedDamage` over the offered options. Falls back to the prompt's first option if the echoed intent/weapon can't be resolved (defensive; shouldn't happen for a `damage_type:` prompt, whose intent is always the firing `RequestAttack`). */
export function decideVariableDamageType(args: {
  prompt: PromptRequest;
  state: EncounterState;
  overlay?: LoadedOverlay;
}): string {
  const { prompt, state, overlay = SEED_OVERLAY } = args;
  const fallback = prompt.options[0]?.id ?? "kinetic";
  const intent = prompt.intent;
  if (intent.type !== "request_attack") return fallback;
  const attacker: Combatant | undefined = state.combatants[intent.attacker];
  const target: Combatant | undefined = intent.target.kind === "unit" ? state.combatants[intent.target.id] : undefined;
  const weapon = attacker?.base.weapons?.find((w) => weaponInstanceKey(w) === intent.weapon);
  if (!attacker || !target || !weapon) return fallback;
  const weaponRef = intent.weapon ?? weaponInstanceKey(weapon);
  let best: { id: string; expectedDamage: number } | undefined;
  for (const opt of prompt.options) {
    if (!(VARIABLE_DAMAGE_TYPE_OPTIONS as readonly string[]).includes(opt.id)) continue;
    const ev = evaluateAttackEv({
      state,
      attacker,
      target,
      weapon,
      weaponRef,
      accuracy: intent.accuracy,
      damageTypeOverride: opt.id as DamageType,
      overlay,
    });
    if (!best || ev.expectedDamage > best.expectedDamage) best = { id: opt.id, expectedDamage: ev.expectedDamage };
  }
  return best?.id ?? fallback;
}

/** Spotter 2's (Panopticon) turn-end prompt: take the free Lock On, or skip it. */
export const PANOPTICON_PROMPT = "panopticon";
/** Panopticon's second prompt: which character in Sensors and line of sight takes the Lock On. */
export const PANOPTICON_TARGET_PROMPT = "panopticon_target";

/** The live hostile characters `spotter` could Lock On: within its Sensors and line of sight, nearest first. */
function lockOnCandidates(state: EncounterState, spotter: Combatant): { id: string; distance: number }[] {
  if (spotter.pos == null) return [];
  const world = worldFor(state);
  const fromRegion = world.footprint(spotter.pos, spotter.base.size);
  const from = fromRegion.cells();
  const out: { id: string; distance: number }[] = [];
  for (const c of Object.values(state.combatants)) {
    if (c.pos == null || !isCharacter(c) || sideOf(c) === sideOf(spotter) || hasStatus(c.statuses, "Destroyed")) continue;
    const toRegion = world.footprint(c.pos, c.base.size);
    const to = toRegion.cells();
    const distance = world.regionDistance(fromRegion, toRegion);
    if (distance > (spotter.base.sensors ?? 10)) continue;
    const view = terrainVisibility({ space: world, terrain: state.terrain, from, to, edges: state.edges, combatants: state.combatants, areaEffects: state.areaEffects });
    if (view.visible) out.push({ id: c.id, distance });
  }
  return out.sort((a, b) => a.distance - b.distance || a.id.localeCompare(b.id));
}

/**
 * Spotter 2's Panopticon prompts (`t_spotter:2`, a turn-end `choose` then a `choose_from_area`):
 * take the free Lock On when a hostile character is in Sensors and line of sight, else skip; then
 * Lock On the nearest hostile the area offers. `undefined` when the target stage offers no hostile
 * (Locking On an ally would help the enemy), so the caller halts rather than guess.
 */
export function decidePanopticon(args: { prompt: PromptRequest; state: EncounterState }): string | undefined {
  const { prompt, state } = args;
  const spotter = prompt.who === undefined ? undefined : state.combatants[prompt.who];
  if (spotter === undefined) return undefined;
  const candidates = lockOnCandidates(state, spotter);
  if (prompt.promptId === PANOPTICON_PROMPT) return candidates.length > 0 ? "lock_on" : "skip";
  const offered = new Set(prompt.options.map((o) => o.id));
  return candidates.find((c) => offered.has(c.id))?.id;
}

/** The classified decision for one prompt — the dispatch {@link answerPrompt} drives. */
export type PromptDecision =
  | { kind: "answer"; optionId: string }
  | { kind: "checkpoint-decline"; optionId: string }
  | { kind: "checkpoint-take"; optionId: string; watcherAttack: RequestAttack }
  | { kind: "unhandled" };

/** Classify `prompt` and decide its answer — pure, no submission. The five named classes (checkpoint, Brace/interception, variable-damage-type, System Trauma, structure-loss interception) are each recognized by the SAME exported shape predicates the engine itself uses (`isMoveCheckpointPromptId`, `prompt.incomingDamage`, `isVariableDamagePromptId`, `isSystemTraumaPromptId`, `isStructureInterceptPromptId`) — never a re-derived heuristic, and the last two answer with the engine's own `firstValidSystemTraumaOption` / `INTERCEPT_SAVE_OPTION` rather than a locally-restated rule. **A move-checkpoint prompt is further routed on `moveCheckpointConsumerId`, never on `isMoveCheckpointPromptId` alone**: an explicit allow-list of the two known consumers, `{ kind: "unhandled" }` for anything else, so a future checkpoint consumer halts until taught rather than silently inheriting the Overwatch read. */
export function decidePromptAnswer(args: {
  prompt: PromptRequest;
  state: EncounterState;
  overlay?: LoadedOverlay;
}): PromptDecision {
  const { prompt, state, overlay = SEED_OVERLAY } = args;
  if (isMoveCheckpointPromptId(prompt.promptId)) {
    const consumerId = moveCheckpointConsumerId(prompt.promptId);
    if (consumerId === OVERWATCH_CHECKPOINT_ID) {
      const decision = decideOverwatchCheckpoint({ state, prompt, overlay });
      return decision.kind === "take"
        ? { kind: "checkpoint-take", optionId: OVERWATCH_TAKE_OPTION, watcherAttack: decision.watcherAttack }
        : { kind: "checkpoint-decline", optionId: OVERWATCH_DECLINE_OPTION };
    }
    // The "take the shot now" prompt of a move Covering Fire split (`COVERING_FIRE_SPLIT_ID`) is
    // the same read at the held cell: the echoed intent names the same mover.
    if (consumerId === AUTHORED_MOVE_REACTION_ID || consumerId === COVERING_FIRE_SPLIT_ID) {
      const decision = decideMoveReactionOffer({ state, prompt, overlay });
      return decision.kind === "take"
        ? { kind: "checkpoint-take", optionId: MOVE_REACTION_TAKE_OPTION, watcherAttack: decision.watcherAttack }
        : { kind: "checkpoint-decline", optionId: MOVE_REACTION_DECLINE_OPTION };
    }
    return { kind: "unhandled" }; // an unrecognized checkpoint consumer — never a silent Overwatch default.
  }
  if (prompt.incomingDamage) {
    return { kind: "answer", optionId: decideInterceptionOffer(prompt) };
  }
  if (isVariableDamagePromptId(prompt.promptId)) {
    return { kind: "answer", optionId: decideVariableDamageType({ prompt, state, overlay }) };
  }
  if (isSystemTraumaPromptId(prompt.promptId)) {
    // The engine's OWN deterministic pick, through the engine's own export — not a re-derived
    // "take the first option" heuristic that happens to land on the same id.
    const optionId = firstValidSystemTraumaOption(prompt.options);
    return optionId === undefined ? { kind: "unhandled" } : { kind: "answer", optionId };
  }
  if (prompt.promptId === PANOPTICON_PROMPT || prompt.promptId === PANOPTICON_TARGET_PROMPT) {
    const optionId = decidePanopticon({ prompt, state });
    return optionId === undefined ? { kind: "unhandled" } : { kind: "answer", optionId };
  }
  if (isStructureInterceptPromptId(prompt.promptId)) {
    // Decline — `gatherStructureIntercept`'s own non-interactive default, through the same
    // exported constant. An offer that does not carry `save` is a shape this policy has not been
    // taught, so it halts rather than picking whatever else is on the list.
    return prompt.options.some((o) => o.id === INTERCEPT_SAVE_OPTION)
      ? { kind: "answer", optionId: INTERCEPT_SAVE_OPTION }
      : { kind: "unhandled" };
  }
  return { kind: "unhandled" };
}

/**
 * Whether this intent can record a prompt answer at all ({@link CHOICE_CARRYING_INTENT_TYPES}, shared
 * with the CLI drive loop). The CLI rejects an unmergeable intent loudly; {@link withRecordedChoice}
 * returns it unchanged. That posture difference lives here in the consumer, not in the shared set.
 */
function carriesChoices(intent: EncounterIntent): intent is ChoiceCarryingIntent {
  return Object.hasOwn(CHOICE_CARRYING_INTENT_TYPES, intent.type);
}

/**
 * Re-submit `intent` with `choices[promptId] = optionId` merged in — the ONE recorded-answer
 * round-trip shape every mutating verb uses (`matchHandle.ts`'s header doc).
 *
 * **The allow-list is every intent shape that carries `choices`** ({@link
 * CHOICE_CARRYING_INTENT_TYPES}), derived from the wire union rather than from which prompts this
 * policy happens to answer today. It used to be `request_attack`/`request_move` alone, which was
 * already wrong when it was written: `RequestGmDamage` carries `choices` (`encounter-intents.ts`)
 * and the structure-loss interception window and System Trauma both echo it.
 *
 * **An unchanged return is not a no-op.** The caller re-submits whatever comes back; on a
 * prompt-bearing intent the authority raises the identical prompt again and the drive loop spins
 * forever with nothing surfaced ({@link answerPrompt} has no guard bound; `chanceNode.ts`'s
 * `resolveSyncPrompts` degrades to `null` after 8 rounds). So the unchanged return is reserved for
 * an intent shape that has no `choices` field to merge into at all — a shape that cannot record an
 * answer is a wiring bug at the prompt's source, not something this merge can fix.
 *
 * Exported rather than file-local because `search.ts`'s chance-node bucketing needs the exact same
 * merge to answer a prompt it hits mid-realization over its own SYNCHRONOUS
 * `DirectRefereeHandle.submitSync` loop.
 */
export function withRecordedChoice(wire: EncounterIntent, promptId: string, optionId: string): EncounterIntent {
  const intent = unwrapForced(wire);
  if (!carriesChoices(intent)) return wire;
  return mapForcedIntent(wire, () => ({ ...intent, choices: { ...(intent.choices ?? {}), [promptId]: optionId } }));
}

/** What {@link answerPrompt} produced. */
export type PromptPolicyOutcome =
  /** Every prompt in the round-trip chain was answered; `observation` is the final, prompt-free result. */
  | { kind: "resolved"; observation: MatchObservation }
  /** A prompt in the chain matched none of the handled classes — halted without guessing. */
  | { kind: "unhandled"; prompt: PromptRequest };

/**
 * Answer `observation.prompt` (and any FURTHER prompt the answer's own submission(s) raise —
 * a checkpoint offer's sibling attack can itself halt on the mover's own Brace, and a
 * `request_move` with several eligible watchers raises one checkpoint offer at a time) by
 * composing {@link MatchHandle.submit} — never a new seam method. Loops until the chain is
 * prompt-free or an unhandled prompt is hit.
 */
export async function answerPrompt(args: {
  handle: MatchHandle;
  observation: MatchObservation;
  overlay?: LoadedOverlay;
}): Promise<PromptPolicyOutcome> {
  const { handle, overlay = SEED_OVERLAY } = args;
  let obs = args.observation;
  while (obs.prompt) {
    const prompt = obs.prompt;
    const decision = decidePromptAnswer({ prompt, state: obs.state, overlay });
    if (decision.kind === "unhandled") return { kind: "unhandled", prompt };

    if (decision.kind === "checkpoint-take") {
      // The interleaving contract: the watcher's shot resolves to completion FIRST, against
      // the still-unmoved mover, before the checkpoint answer itself re-submits.
      const siblingObs = await handle.submit({ kind: "intent", intent: decision.watcherAttack });
      if (siblingObs.rejection) return { kind: "unhandled", prompt }; // the chosen weapon/target turned out illegal server-side — surface rather than guess further.
      if (siblingObs.prompt) {
        const resolvedSibling = await answerPrompt({ handle, observation: siblingObs, overlay });
        if (resolvedSibling.kind === "unhandled") return resolvedSibling;
      }
    }

    const answeredIntent = withRecordedChoice(prompt.intent, prompt.promptId, decision.optionId);
    obs = await handle.submit({ kind: "intent", intent: answeredIntent });
  }
  return { kind: "resolved", observation: obs };
}
